import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Modal, ModalBody } from "reactstrap";
import QuickActionsSidebar from "../DistributorDashboard/QuickActionsSidebar";
import { getAdminActions, getDistributorActions } from "../../../config/actions";
import { API_URL } from "../../../config";
import "../DistributorsAdd/DistributorsAdd.css";

const SiteSettings = () => {
  const { distributor_id: md5Id } = useParams();
  const navigate = useNavigate();
  const user_type = localStorage.getItem("user_type");
  const userId = localStorage.getItem("user_id");

  const isAdmin = user_type === "7";
  const sidebarActions = isAdmin
    ? getAdminActions(userId)
    : getDistributorActions(userId);
  const backPath = isAdmin ? "/distributors" : "/mini-cart";

  const [expandedMenu, setExpandedMenu] = useState(null);

  // Resolved from the MD5 route param
  const [distributorId, setDistributorId] = useState(null);
  const [resolveError, setResolveError] = useState(false);

  // Announcement state
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const [announcementDesc, setAnnouncementDesc] = useState("");
  const [announcementEnabled, setAnnouncementEnabled] = useState(false);
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  const [announcementLoading, setAnnouncementLoading] = useState(true);

  // Meta state
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDesc, setMetaDesc] = useState("");
  const [metaFavicon, setMetaFavicon] = useState("");
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaLoading, setMetaLoading] = useState(true);
  const [faviconUploading, setFaviconUploading] = useState(false);

  // Success modal
  const [successModal, setSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchSettings();
  }, [md5Id]);

  const fetchSettings = async () => {
    if (!md5Id || !userId) return;
    try {
      const res = await fetch(`${API_URL}/distributor/site/${userId}`, {
        headers: { "x-access-token": localStorage.getItem("accessToken") },
      });
      const data = await res.json();
      const s = data.status && Array.isArray(data.data)
        ? data.data.find((site) => site.site_id === md5Id)
        : null;
      if (s) {
        setDistributorId(s.id);
        setAnnouncementTitle(s.announcement_title || "");
        setAnnouncementDesc(s.announcement_desc || "");
        const hasExistingAnnouncement = !!(s.announcement_title || s.announcement_desc);
        setAnnouncementEnabled(hasExistingAnnouncement ? !!s.announcement_enabled : true);
        setMetaTitle(s.meta_title || "");
        setMetaDesc(s.meta_desc || "");
        setMetaFavicon(s.meta_favicon || "");
      } else {
        setResolveError(true);
      }
    } catch (err) {
      console.error("Error fetching site settings:", err);
      setResolveError(true);
    } finally {
      setAnnouncementLoading(false);
      setMetaLoading(false);
    }
  };

  const saveAnnouncement = async () => {
    setAnnouncementSaving(true);
    try {
      const res = await fetch(`${API_URL}/distributor/announcement/${md5Id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          announcement_title: announcementTitle,
          announcement_desc: announcementDesc,
          announcement_enabled: announcementEnabled ? "1" : "0",
        }),
      });
      const data = await res.json();
      if (data.status) {
        setSuccessMessage("Announcement settings saved!");
        setSuccessModal(true);
      } else {
        alert(data.message || "Failed to save");
      }
    } catch (err) {
      alert("Network error. Please try again.");
    } finally {
      setAnnouncementSaving(false);
    }
  };

  const saveMeta = async () => {
    setMetaSaving(true);
    try {
      const res = await fetch(`${API_URL}/distributor/meta/${md5Id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          meta_title: metaTitle,
          meta_desc: metaDesc,
          meta_favicon: metaFavicon,
        }),
      });
      const data = await res.json();
      if (data.status) {
        setSuccessMessage("Meta settings saved!");
        setSuccessModal(true);
      } else {
        alert(data.message || "Failed to save");
      }
    } catch (err) {
      alert("Network error. Please try again.");
    } finally {
      setMetaSaving(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setFaviconUploading(true);
    try {
      const formData = new FormData();
      formData.append("site_id", md5Id);
      formData.append("favicon", file);

      const res = await fetch(`${API_URL}/distributor/meta/favicon/upload`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.status && data.s3url) {
        setMetaFavicon(data.s3url);
      } else {
        alert(data.message || "Failed to upload favicon");
      }
    } catch (err) {
      alert("Network error. Please try again.");
    } finally {
      setFaviconUploading(false);
    }
  };

  return (
    <div className="distributors-add-container">
      <div className="distributors-add-dashboard">
        <QuickActionsSidebar
          actions={sidebarActions}
          expandedMenu={expandedMenu}
          setExpandedMenu={setExpandedMenu}
        />

        <div className="distributors-add-content">
          {/* Header */}
          <div className="add-btn-wrp add-btn-wrp2">
            <div className="head-listing">
              <button
                onClick={() => navigate(backPath)}
                style={{
                  background: "none",
                  border: "none",
                  color: "rgb(77, 0, 77)",
                  fontWeight: "600",
                  cursor: "pointer",
                  marginBottom: "8px",
                  padding: 0,
                  fontSize: "14px",
                }}
              >
                ← Back
              </button>
              <h3>Site Settings</h3>
            </div>
          </div>

          {resolveError ? (
            <div style={{ padding: "32px", textAlign: "center", color: "#6b7280" }}>
              Distributor not found.
            </div>
          ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "0 0 32px" }}>

            {/* ── Announcements ── */}
            <div style={{
              background: "white",
              border: "2px solid #e9d5ff",
              borderRadius: "16px",
              padding: "28px 32px",
              boxShadow: "0 4px 12px rgba(168, 85, 247, 0.08)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
                <div style={{
                  width: "40px", height: "40px", borderRadius: "10px",
                  background: "rgb(77, 0, 77)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <i className="fas fa-bullhorn" style={{ color: "white", fontSize: "18px" }}></i>
                </div>
                <div>
                  <h4 style={{ margin: 0, fontWeight: "700", color: "#1f2937" }}>Announcement</h4>
                  {/* <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>Show a banner announcement on your storefront</p> */}
                </div>
                {/* Toggle */}
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "13px", color: "#6b7280" }}>
                    {announcementEnabled ? "Enabled" : "Disabled"}
                  </span>
                  <div
                    onClick={() => setAnnouncementEnabled(!announcementEnabled)}
                    style={{
                      width: "48px", height: "26px", borderRadius: "13px", cursor: "pointer",
                      background: announcementEnabled ? "rgb(77, 0, 77)" : "#d1d5db",
                      position: "relative", transition: "background 0.2s",
                    }}
                  >
                    <div style={{
                      position: "absolute", top: "3px",
                      left: announcementEnabled ? "25px" : "3px",
                      width: "20px", height: "20px", borderRadius: "50%",
                      background: "white", transition: "left 0.2s",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                    }} />
                  </div>
                </div>
              </div>

              {announcementLoading ? (
                <div className="loading-state"><div className="loader-spinner"></div></div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div>
                    <label style={{ fontWeight: "600", marginBottom: "6px", display: "block", fontSize: "14px" }}>
                      <i className="fa fa-heading" style={{ color: "rgb(77, 0, 77)", marginRight: "6px" }}></i>
                      Title
                    </label>
                    <input
                      type="text"
                      value={announcementTitle}
                      onChange={(e) => setAnnouncementTitle(e.target.value)}
                      placeholder="e.g. Free shipping on orders over $50!"
                      style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "14px", boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: "600", marginBottom: "6px", display: "block", fontSize: "14px" }}>
                      <i className="fa fa-align-left" style={{ color: "rgb(77, 0, 77)", marginRight: "6px" }}></i>
                      Description
                    </label>
                    <textarea
                      value={announcementDesc}
                      onChange={(e) => setAnnouncementDesc(e.target.value)}
                      placeholder="Add more details about your announcement..."
                      rows={3}
                      style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      onClick={saveAnnouncement}
                      disabled={announcementSaving}
                      style={{
                        background: announcementSaving ? "#a78bfa" : "rgb(77, 0, 77)",
                        color: "white", border: "none", borderRadius: "8px",
                        padding: "10px 24px", fontWeight: "600", cursor: "pointer", fontSize: "14px",
                      }}
                    >
                      {announcementSaving ? "Saving..." : "Save Announcement"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Meta ── */}
            <div style={{
              background: "white",
              border: "2px solid #e9d5ff",
              borderRadius: "16px",
              padding: "28px 32px",
              boxShadow: "0 4px 12px rgba(168, 85, 247, 0.08)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" }}>
                <div style={{
                  width: "40px", height: "40px", borderRadius: "10px",
                  background: "rgb(77, 0, 77)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <i className="fas fa-code" style={{ color: "white", fontSize: "18px" }}></i>
                </div>
                <div>
                  <h4 style={{ margin: 0, fontWeight: "700", color: "#1f2937" }}>Meta & SEO</h4>
                  {/* <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>Page title, description and favicon for your storefront</p> */}
                </div>
              </div>

              {metaLoading ? (
                <div className="loading-state"><div className="loader-spinner"></div></div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div>
                    <label style={{ fontWeight: "600", marginBottom: "6px", display: "block", fontSize: "14px" }}>
                      <i className="fa fa-heading" style={{ color: "rgb(77, 0, 77)", marginRight: "6px" }}></i>
                      Meta Title
                    </label>
                    <input
                      type="text"
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                      placeholder="e.g. My Awesome Store"
                      style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "14px", boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: "600", marginBottom: "6px", display: "block", fontSize: "14px" }}>
                      <i className="fa fa-align-left" style={{ color: "rgb(77, 0, 77)", marginRight: "6px" }}></i>
                      Meta Description
                    </label>
                    <textarea
                      value={metaDesc}
                      onChange={(e) => setMetaDesc(e.target.value)}
                      placeholder="Brief description shown in search engine results..."
                      rows={3}
                      style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }}
                    />
                  </div>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                      <label style={{ fontWeight: "600", fontSize: "14px", margin: 0 }}>
                        <i className="fa fa-image" style={{ color: "rgb(77, 0, 77)", marginRight: "6px" }}></i>
                        Favicon URL
                      </label>
                      <label
                        htmlFor="favicon-upload-input"
                        style={{
                          display: "flex", alignItems: "center", gap: "6px",
                          fontSize: "13px", fontWeight: "600", color: "rgb(77, 0, 77)",
                          cursor: faviconUploading ? "not-allowed" : "pointer",
                          opacity: faviconUploading ? 0.6 : 1,
                          margin: 0, padding: "4px 8px", borderRadius: "6px", border: "1px solid rgb(77, 0, 77)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        {faviconUploading ? (
                          <>
                            <span className="loader-spinner" style={{ width: "14px", height: "14px", borderWidth: "2px",margin:0 }}></span>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-upload"></i>
                            Upload
                          </>
                        )}
                      </label>
                      <input
                        id="favicon-upload-input"
                        type="file"
                        accept="image/*"
                        onChange={handleFaviconUpload}
                        disabled={faviconUploading}
                        style={{ display: "none" }}
                      />
                    </div>
                    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                      <input
                        type="url"
                        value={metaFavicon}
                        onChange={(e) => setMetaFavicon(e.target.value)}
                        placeholder="https://yourdomain.com/favicon.ico"
                        style={{ flex: 1, padding: "10px 14px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "14px" }}
                      />
                      {metaFavicon && (
                        <img
                          src={metaFavicon}
                          alt="favicon preview"
                          style={{ width: "32px", height: "32px", borderRadius: "6px", border: "1px solid #e5e7eb", objectFit: "contain" }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      onClick={saveMeta}
                      disabled={metaSaving}
                      style={{
                        background: metaSaving ? "#a78bfa" : "rgb(77, 0, 77)",
                        color: "white", border: "none", borderRadius: "8px",
                        padding: "10px 24px", fontWeight: "600", cursor: "pointer", fontSize: "14px",
                      }}
                    >
                      {metaSaving ? "Saving..." : "Save Meta"}
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
          )}
        </div>
      </div>

      {/* Success Modal */}
      <Modal isOpen={successModal} toggle={() => setSuccessModal(false)} centered>
        <ModalBody className="text-center">
          <div style={{ fontSize: "40px", color: "rgb(77, 0, 77)" }}>✔</div>
          <h5 className="mt-2">{successMessage}</h5>
          <button
            className="btn btn-success mt-3"
            style={{ backgroundColor: "rgb(77, 0, 77)", borderColor: "rgb(77, 0, 77)" }}
            onClick={() => setSuccessModal(false)}
          >
            OK
          </button>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default SiteSettings;
